import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { CreditCard, Download, ExternalLink, Loader2, Crown, Calendar, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface BillingDashboardProps {
  accessToken: string;
  firmId: string;
}

interface Subscription {
  id: string;
  status: string;
  current_period_end: number;
  trial_end?: number;
  cancel_at_period_end: boolean;
  items: {
    data: Array<{
      price: {
        id: string;
        unit_amount: number;
        recurring: {
          interval: string;
        };
      };
    }>;
  };
}

interface Invoice {
  id: string;
  number: string;
  amount_paid: number;
  amount_due: number;
  status: string;
  created: number;
  invoice_pdf: string;
  hosted_invoice_url: string;
}

export function BillingDashboard({ accessToken, firmId }: BillingDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [firmData, setFirmData] = useState<any>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [processingPortal, setProcessingPortal] = useState(false);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load subscription
      const subResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/billing/subscription`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (subResponse.ok) {
        const data = await subResponse.json();
        setSubscription(data.subscription);
        setFirmData(data.firm);
      }

      // Load invoices
      const invoicesResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/billing/invoices`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (invoicesResponse.ok) {
        const data = await invoicesResponse.json();
        setInvoices(data.invoices);
      }
    } catch (err: any) {
      console.error('Error loading billing data:', err);
      setError(err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    setProcessingPortal(true);
    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/billing/create-portal`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.requiresSubscription) {
          setError('Please subscribe to a plan first. Use Change Plan on the Current Subscription card above.');
          setProcessingPortal(false);
          return;
        }
        throw new Error(errorData.error || 'Failed to create billing portal session');
      }

      const data = await response.json();
      window.location.href = data.portalUrl;
    } catch (err: any) {
      console.error('Error opening billing portal:', err);
      setError(err.message || 'Failed to open billing portal');
      setProcessingPortal(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-600"><CheckCircle2 className="h-3 w-3 mr-1" />Active</Badge>;
      case 'trialing':
        return <Badge className="bg-blue-600"><Calendar className="h-3 w-3 mr-1" />Trial</Badge>;
      case 'past_due':
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Past Due</Badge>;
      case 'canceled':
        return <Badge variant="outline"><XCircle className="h-3 w-3 mr-1" />Canceled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'solo':
        return <Badge variant="secondary"><Crown className="h-3 w-3 mr-1" />Solo</Badge>;
      case 'starter':
        return <Badge variant="secondary"><Crown className="h-3 w-3 mr-1" />Starter</Badge>;
      case 'premium':
        return <Badge className="bg-blue-600"><Crown className="h-3 w-3 mr-1" />Premium</Badge>;
      case 'enterprise':
        return <Badge className="bg-purple-600"><Crown className="h-3 w-3 mr-1" />Enterprise</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading billing information...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Current Subscription */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Current Subscription</CardTitle>
              <CardDescription>Manage your billing and subscription</CardDescription>
            </div>
            <Button onClick={openBillingPortal} disabled={processingPortal}>
              {processingPortal ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Opening...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Manage Billing
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscription ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Plan</p>
                  {getTierBadge(firmData?.subscriptionTier)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  {getStatusBadge(subscription.status)}
                </div>
                {subscription.trial_end && subscription.status === 'trialing' && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Trial Ends</p>
                    <p>{formatDate(subscription.trial_end)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    {subscription.cancel_at_period_end ? 'Cancels On' : 'Renews On'}
                  </p>
                  <p>{formatDate(subscription.current_period_end)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Price</p>
                  <p>
                    {formatCurrency(subscription.items.data[0]?.price.unit_amount || 0)}
                    <span className="text-muted-foreground">
                      /{subscription.items.data[0]?.price.recurring.interval}
                    </span>
                  </p>
                </div>
              </div>

              {subscription.cancel_at_period_end && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your subscription will be canceled on {formatDate(subscription.current_period_end)}.
                    You can reactivate it anytime before this date through the billing portal.
                  </AlertDescription>
                </Alert>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Crown className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">No active subscription</p>
              <p className="text-sm text-muted-foreground">
                Currently on the <strong>Free</strong> plan
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice History */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice History</CardTitle>
          <CardDescription>View and download your invoices</CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No invoices yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.number || invoice.id}</TableCell>
                    <TableCell>{formatDate(invoice.created)}</TableCell>
                    <TableCell>{formatCurrency(invoice.amount_paid)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={invoice.status === 'paid' ? 'default' : 'destructive'}
                      >
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      {invoice.hosted_invoice_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      )}
                      {invoice.invoice_pdf && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
